import { X, TrendingUp, TrendingDown, AlertTriangle, CheckCircle, Info, Pencil, Check, RotateCcw, X as XIcon } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { useExerciseLanding, ExerciseLanding, BudgetLineForecast } from '../hooks/useExerciseLanding';

interface ExerciseLandingDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  year: number;
}

interface EditState {
  lineId: string;
  value: string;
}

export function ExerciseLandingDrawer({ isOpen, onClose, year }: ExerciseLandingDrawerProps) {
  const {
    isLoading,
    exerciseLanding,
    lineForecasts,
    loadExerciseLanding,
    loadAllLineForecasts,
    saveOverride,
    deleteOverride,
    generateAnalysisText,
    getStatusColor,
    getStatusLabel,
    getMethodLabel
  } = useExerciseLanding();

  const [selectedTab, setSelectedTab] = useState<'summary' | 'details'>('summary');
  const [editState, setEditState] = useState<EditState | null>(null);
  const [savingId, setSavingId] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      loadExerciseLanding(year);
      loadAllLineForecasts(year);
    }
  }, [isOpen, year, loadExerciseLanding, loadAllLineForecasts]);

  useEffect(() => {
    if (editState && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editState?.lineId]);

  if (!isOpen) return null;

  const fmt = (amount: number) =>
    new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

  const fmtPct = (percent: number) =>
    `${percent >= 0 ? '+' : ''}${percent.toFixed(1)}%`;

  const startEdit = (line: BudgetLineForecast) => {
    setEditState({ lineId: line.budget_line_id, value: line.forecast_amount.toFixed(2) });
  };

  const cancelEdit = () => setEditState(null);

  const confirmEdit = async (line: BudgetLineForecast) => {
    if (!editState) return;
    const amount = parseFloat(editState.value.replace(',', '.'));
    if (isNaN(amount)) { cancelEdit(); return; }
    setSavingId(line.budget_line_id);
    const ok = await saveOverride(line.budget_line_id, line.budget_line_year, amount);
    setSavingId(null);
    setEditState(null);
    if (ok) {
      await loadAllLineForecasts(year);
      await loadExerciseLanding(year);
    }
  };

  const resetOverride = async (line: BudgetLineForecast) => {
    setSavingId(line.budget_line_id);
    const ok = await deleteOverride(line.budget_line_id, line.budget_line_year);
    setSavingId(null);
    if (ok) {
      await loadAllLineForecasts(year);
      await loadExerciseLanding(year);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, line: BudgetLineForecast) => {
    if (e.key === 'Enter') confirmEdit(line);
    if (e.key === 'Escape') cancelEdit();
  };

  const getMethodBadgeColor = (method: string) => {
    switch (method) {
      case 'MANUAL': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'RUN_RATE': return 'bg-gray-100 text-gray-600 border-gray-200';
      case 'PRORATA': return 'bg-orange-100 text-orange-600 border-orange-200';
      case 'NO_DATA': return 'bg-gray-100 text-gray-400 border-gray-200';
      default: return 'bg-gray-100 text-gray-600 border-gray-200';
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      <div className="absolute inset-0 bg-gray-900 bg-opacity-50 transition-opacity" onClick={onClose} />

      <div className="absolute inset-y-0 right-0 max-w-5xl w-full flex">
        <div className="w-full bg-white shadow-xl flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">Atterrissage Fin d'Exercice</h2>
                <p className="text-sm text-gray-600 mt-1">Projection budgétaire pour l'exercice {year}</p>
              </div>
              <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <X className="h-6 w-6 text-gray-500" />
              </button>
            </div>
          </div>

          <div className="px-6 border-b border-gray-200 bg-gray-50">
            <div className="flex gap-4">
              <button
                onClick={() => setSelectedTab('summary')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  selectedTab === 'summary' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Vue d'ensemble
              </button>
              <button
                onClick={() => setSelectedTab('details')}
                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                  selectedTab === 'details' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-900'
                }`}
              >
                Détail par ligne ({lineForecasts.length})
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-6">
            {isLoading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            ) : exerciseLanding ? (
              <>
                {selectedTab === 'summary' && (
                  <SummaryTab
                    exerciseLanding={exerciseLanding}
                    fmt={fmt}
                    generateAnalysisText={generateAnalysisText}
                  />
                )}

                {selectedTab === 'details' && (
                  <div>
                    <div className="mb-4 flex items-start justify-between gap-4">
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        Cliquez sur <Pencil className="inline h-3.5 w-3.5 text-blue-500" /> pour remplacer la projection calculée par une valeur manuelle.
                        Appuyez sur <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono">Entrée</kbd> pour confirmer ou <kbd className="px-1 py-0.5 bg-gray-100 border border-gray-300 rounded text-xs font-mono">Échap</kbd> pour annuler.
                      </p>
                      <span className="text-xs text-gray-400 bg-gray-100 border border-gray-200 rounded px-2 py-1 shrink-0">
                        Tri par écart décroissant
                      </span>
                    </div>

                    {lineForecasts.length === 0 ? (
                      <div className="text-center py-12 text-gray-500">
                        <Info className="h-12 w-12 mx-auto mb-3 opacity-50" />
                        <p>Aucune ligne budgétaire trouvée pour cet exercice</p>
                      </div>
                    ) : (
                      <div className="overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ligne Budgétaire</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Budget Initial</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Réalisé</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Projection</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Écart</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Méthode</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {lineForecasts.map((line) => {
                              const isEditing = editState?.lineId === line.budget_line_id;
                              const isSaving = savingId === line.budget_line_id;

                              return (
                                <tr
                                  key={line.budget_line_id}
                                  className={`transition-colors ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                >
                                  <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                    <div className="flex items-center gap-2">
                                      {line.has_override && (
                                        <span
                                          className="w-2 h-2 rounded-full bg-blue-500 shrink-0"
                                          title="Valeur modifiée manuellement"
                                        />
                                      )}
                                      {line.category_name}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-700">{fmt(line.budget_amount)}</td>
                                  <td className="px-4 py-3 text-sm text-right text-gray-700">{fmt(line.realized_amount)}</td>
                                  <td className="px-4 py-3 text-sm text-right">
                                    {isEditing ? (
                                      <input
                                        ref={inputRef}
                                        type="number"
                                        step="0.01"
                                        value={editState.value}
                                        onChange={e => setEditState({ ...editState, value: e.target.value })}
                                        onKeyDown={e => handleKeyDown(e, line)}
                                        className="w-32 px-2 py-1 border-2 border-blue-400 rounded text-sm text-right bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
                                        disabled={isSaving}
                                      />
                                    ) : (
                                      <span className={`font-medium ${line.has_override ? 'text-blue-700' : 'text-gray-900'}`}>
                                        {fmt(line.forecast_amount)}
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-right">
                                    <div className={`font-medium ${line.variance_amount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                                      {line.variance_amount >= 0 ? '+' : ''}{fmt(line.variance_amount)}
                                    </div>
                                    <div className={`text-xs ${line.variance_amount > 0 ? 'text-red-400' : 'text-green-400'}`}>
                                      {fmtPct(line.variance_percent)}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(line.status)}`}>
                                      {line.status === 'ON_TRACK'
                                        ? <CheckCircle className="h-3.5 w-3.5" />
                                        : <AlertTriangle className="h-3.5 w-3.5" />}
                                      {getStatusLabel(line.status)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium border ${getMethodBadgeColor(line.method)}`}>
                                      {getMethodLabel(line.method)}
                                    </span>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {isSaving ? (
                                      <div className="flex justify-center">
                                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
                                      </div>
                                    ) : isEditing ? (
                                      <div className="flex items-center justify-center gap-1">
                                        <button
                                          onClick={() => confirmEdit(line)}
                                          className="p-1 text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                                          title="Confirmer"
                                        >
                                          <Check className="h-4 w-4" />
                                        </button>
                                        <button
                                          onClick={cancelEdit}
                                          className="p-1 text-gray-400 hover:bg-gray-100 rounded transition-colors"
                                          title="Annuler"
                                        >
                                          <XIcon className="h-4 w-4" />
                                        </button>
                                      </div>
                                    ) : (
                                      <div className="flex items-center justify-center gap-1">
                                        <button
                                          onClick={() => startEdit(line)}
                                          className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                          title="Modifier la projection"
                                        >
                                          <Pencil className="h-4 w-4" />
                                        </button>
                                        {line.has_override && (
                                          <button
                                            onClick={() => resetOverride(line)}
                                            className="p-1 text-gray-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                                            title="Réinitialiser (recalcul automatique)"
                                          >
                                            <RotateCcw className="h-4 w-4" />
                                          </button>
                                        )}
                                      </div>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {lineForecasts.some(l => l.has_override) && (
                      <div className="mt-4 flex items-center gap-2 text-xs text-gray-500 bg-blue-50 border border-blue-100 rounded-lg px-3 py-2">
                        <span className="w-2 h-2 rounded-full bg-blue-500 shrink-0" />
                        Les lignes avec un point bleu ont une projection modifiée manuellement.
                        Cliquez sur <RotateCcw className="inline h-3.5 w-3.5 mx-0.5 text-orange-500" /> pour revenir au calcul automatique.
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12 text-gray-500">
                <Info className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Aucune donnée disponible pour cet exercice</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface SummaryTabProps {
  exerciseLanding: ExerciseLanding;
  fmt: (n: number) => string;
  generateAnalysisText: (l: ExerciseLanding) => string;
}

function SummaryTab({ exerciseLanding, fmt, generateAnalysisText }: SummaryTabProps) {
  const l = exerciseLanding;
  return (
    <div className="space-y-6">
      <div className="border rounded-lg p-6 bg-gradient-to-br from-blue-50 to-white">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold mb-1">Résumé Global</h3>
            <p className="text-sm text-gray-600">
              Du {new Date(l.exercise_start_date).toLocaleDateString('fr-FR')} au{' '}
              {new Date(l.exercise_end_date).toLocaleDateString('fr-FR')}
            </p>
          </div>
        </div>

        <div className="mb-6 p-4 bg-white rounded-lg border-2 border-blue-200">
          <div className="text-sm text-gray-600 mb-2">Solde Prévisionnel</div>
          <div className={`text-3xl font-bold ${l.total_forecast_balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {fmt(l.total_forecast_balance)}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            Écart vs budget : {fmt(l.variance_balance)}
          </div>
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Recettes</h4>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Budget</div>
              <div className="text-base font-bold text-gray-900">{fmt(l.total_budget_income)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Réalisé</div>
              <div className="text-base font-bold text-gray-900">{fmt(l.total_realized_income)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Projection</div>
              <div className="text-base font-bold text-gray-900">{fmt(l.total_forecast_income)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Écart</div>
              <div className={`text-base font-bold flex items-center gap-1 ${l.variance_income >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {l.variance_income >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                {fmt(Math.abs(l.variance_income))}
              </div>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Dépenses</h4>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Budget</div>
              <div className="text-base font-bold text-gray-900">{fmt(l.total_budget_expense)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Réalisé</div>
              <div className="text-base font-bold text-gray-900">{fmt(l.total_realized_expense)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Projection</div>
              <div className="text-base font-bold text-gray-900">{fmt(l.total_forecast_expense)}</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200">
              <div className="text-xs text-gray-600 mb-1">Écart</div>
              <div className={`text-base font-bold flex items-center gap-1 ${l.variance_expense >= 0 ? 'text-red-600' : 'text-green-600'}`}>
                {l.variance_expense >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                {fmt(Math.abs(l.variance_expense))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-600 pt-4 border-t border-gray-200">
          <span>{l.months_elapsed.toFixed(1)} mois écoulés</span>
          <span>•</span>
          <span>{l.months_remaining.toFixed(1)} mois restants</span>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">Analyse</h4>
        <p className="text-blue-800">{generateAnalysisText(l)}</p>
      </div>
    </div>
  );
}
